import React, { useState, useRef, useEffect } from 'react'
import { ChevronDown, Check } from 'lucide-react'

interface DropdownOption {
  value: string
  label: string
  description?: string
}

interface DropdownProps {
  options: DropdownOption[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  className = '',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const selectedOption = options.find(option => option.value === value)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSelect = (optionValue: string) => {
    onChange(optionValue)
    setIsOpen(false)
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full p-3 text-left bg-white border border-gray-300 rounded-lg
          focus:ring-2 focus:ring-[#8438ff] focus:border-transparent
          transition-all duration-200 ease-in-out
          ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:border-[#8438ff]'}
          ${isOpen ? 'ring-2 ring-[#8438ff] border-transparent' : ''}
        `}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            {selectedOption ? (
              <div>
                <div className="font-medium text-[#8438ff]">{selectedOption.label}</div>
                {selectedOption.description && (
                  <div className="text-sm text-gray-500 mt-1">{selectedOption.description}</div>
                )}
              </div>
            ) : (
              <span className="text-gray-500">{placeholder}</span>
            )}
          </div>
          <ChevronDown 
            className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`} 
          />
        </div>
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-[#8438ff]/20 rounded-lg shadow-lg max-h-60 overflow-auto">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={`
                w-full p-3 text-left hover:bg-[#8438ff]/10 transition-colors duration-150
                flex items-center justify-between group
                ${value === option.value ? 'bg-[#8438ff]/10 text-[#8438ff]' : 'text-[#8438ff] hover:text-[#8438ff]'}
                first:rounded-t-lg last:rounded-b-lg
              `}
            >
              <div className="flex-1">
                <div className="font-medium">{option.label}</div>
                {option.description && (
                  <div className={`text-sm mt-1 ${
                    value === option.value ? 'text-[#8438ff]/80' : 'text-[#8438ff]/60 group-hover:text-[#8438ff]/80'
                  }`}>
                    {option.description}
                  </div>
                )}
              </div>
              {value === option.value && (
                <Check className="h-4 w-4 text-[#8438ff] ml-2" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default Dropdown
